import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ModalDialog } from '../../../modal/layout/ModalDialog';
import { ModalHeader } from '../../../modal/layout/ModalHeader';
import { useProtectedClose } from '../../../modal/hooks/useProtectedClose';
import {
    NOTIFICATION_VARIANT,
    NotificationsContainer,
    useNotifications,
} from '../../../notifications/Notifications';
import type { AttributeEntityType, AttributeValueGraphQLValue } from '../contracts';
import { useAttributeMutations } from '../graphql/useAttributeMutations';
import { useUnsavedChangesGuard } from '../../../../hooks/useUnsavedChangesGuard';

interface AttributeCreateModalProps {
    entityType: AttributeEntityType;
    entityId: string;
    onClose: () => void;
    onAfterMutation: () => Promise<void>;
}

interface AttributeCreateFormValues {
    label: string;
    valueType: AttributeValueGraphQLValue;
}

export function AttributeCreateModal({
    entityType,
    entityId,
    onClose,
    onAfterMutation,
}: AttributeCreateModalProps) {
    const allowNextNavigationRef = useRef(false);
    const { addNotification, clearNotifications } = useNotifications();
    const { createDefinition, isMutating } = useAttributeMutations();
    const [isSaving, setIsSaving] = useState(false);
    const {
        formState: { errors, isDirty },
        handleSubmit,
        register,
    } = useForm<AttributeCreateFormValues>({
        defaultValues: {
            label: '',
            valueType: 'TEXT',
        },
    });
    const closeRoute = useCallback(() => {
        allowNextNavigationRef.current = true;
        onClose();
    }, [onClose]);
    const { attemptClose, isShaking, showWarning } = useProtectedClose({
        isOpen: true,
        shouldWarn: isDirty,
        onClose: closeRoute,
        canClose: !isSaving && !isMutating,
    });

    useUnsavedChangesGuard({
        shouldWarn: isDirty,
        allowNextNavigationRef,
    });

    useEffect(() => {
        if (showWarning) {
            addNotification({
                message: gettext('UNSAVED_CHANGES_CLOSE_AGAIN'),
                variant: NOTIFICATION_VARIANT.WARNING,
            });
        } else {
            clearNotifications();
        }
    }, [addNotification, clearNotifications, showWarning]);

    async function createAttribute(values: AttributeCreateFormValues) {
        setIsSaving(true);
        try {
            await createDefinition(
                { entityType, entityId },
                { ...values, label: values.label.trim() }
            );
            await onAfterMutation();
            closeRoute();
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <ModalDialog
            open={true}
            onClose={attemptClose}
            canClose={!isSaving && !isMutating}
            closeOnClickOutside={true}
            closeOnEsc={true}
            shake={isShaking}
            className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl"
            aria-labelledby="attribute-create-modal-title"
        >
            <div className="relative flex flex-col py-6">
                <ModalHeader
                    onClose={attemptClose}
                    title={gettext('ATTRIBUTE_CREATE_TITLE')}
                    isLoading={isSaving || isMutating}
                />
                <div className="px-6 pb-4">
                    <NotificationsContainer />
                </div>
                <form
                    className="space-y-5 px-6"
                    onSubmit={(event) => void handleSubmit(createAttribute)(event)}
                >
                    <div>
                        <label
                            className="block text-sm font-semibold text-gray-700"
                            htmlFor="attribute-create-label"
                        >
                            {gettext('ATTRIBUTE_LABEL_FIELD')}
                        </label>
                        <input
                            id="attribute-create-label"
                            className="focus:border-primary focus:ring-primary/30 mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-inner focus:outline-none focus:ring-2"
                            aria-invalid={Boolean(errors.label)}
                            aria-describedby={
                                errors.label ? 'attribute-create-label-error' : undefined
                            }
                            {...register('label', {
                                validate: (value) =>
                                    Boolean(value.trim()) || gettext('FIELD_REQUIRED_VALIDATION'),
                            })}
                        />
                        {errors.label && (
                            <p
                                id="attribute-create-label-error"
                                role="alert"
                                className="mt-2 text-sm text-red-600"
                            >
                                {errors.label.message}
                            </p>
                        )}
                    </div>
                    <div>
                        <label
                            className="block text-sm font-semibold text-gray-700"
                            htmlFor="attribute-create-value-type"
                        >
                            {gettext('ATTRIBUTE_TYPE_FIELD')}
                        </label>
                        <select
                            id="attribute-create-value-type"
                            className="focus:border-primary focus:ring-primary/30 mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-inner focus:outline-none focus:ring-2"
                            {...register('valueType', { required: true })}
                        >
                            <option value="TEXT">{gettext('ATTRIBUTE_VALUE_TYPE_TEXT')}</option>
                            <option value="IMAGE">{gettext('ATTRIBUTE_VALUE_TYPE_IMAGE')}</option>
                            <option value="IMAGE_LIST">
                                {gettext('ATTRIBUTE_VALUE_TYPE_IMAGE_LIST')}
                            </option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
                        <button
                            type="button"
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                            onClick={attemptClose}
                            disabled={isSaving || isMutating}
                        >
                            {gettext('CANCEL')}
                        </button>
                        <button
                            type="submit"
                            className="bg-primary rounded-lg px-4 py-2 text-sm font-semibold text-black disabled:opacity-50"
                            disabled={isSaving || isMutating}
                        >
                            {gettext('ATTRIBUTE_CREATE_SUBMIT')}
                        </button>
                    </div>
                </form>
            </div>
        </ModalDialog>
    );
}
